import along from "@turf/along";

/**
 * What a leg writes to the map, in one place.
 *
 * PathSequence draws a leg frame by frame and writes all of this in its render body. That works in
 * one tab rendering the film in order, and fails in a tab that starts partway in: PathSequence is
 * only mounted while its own leg is on screen, so a tab that never ran a leg never draws that leg's
 * line, and the transport marker never lands where the leg ended. FinishedSequences (next door) puts those
 * writes back for the legs a tab skipped, and it has to put back exactly what PathSequence would have
 * left, so both call the helpers below -- PathSequence with the phase it is at, FinishedSequences with the
 * phase of a leg's last frame. When these were two copies of the same writes, the first change to one
 * of them was going to be a render that differed by tab.
 *
 * No `remotion` import on purpose: nothing here depends on the frame, only on a phase handed in.
 */

export const isValidCoord = (val: any): val is number => typeof val === "number" && !isNaN(val) && isFinite(val);

/**
 * The phase PathSequence is at on a leg's last frame: `(pathFrame + 1) / MAP_DURATION` with
 * `pathFrame = MAP_DURATION - 1`. It is the leg complete, which is what is left on the map once the
 * leg is over.
 */
export const LEG_FINAL_PHASE = 1;

/**
 * Turn a geodesic progress fraction into the projected one Mapbox's `line-progress` uses.
 *
 * The marker is placed with turf's `along()`, which measures in real kilometres. The line is cut
 * with a `line-gradient` stop on `["line-progress"]`, and Mapbox normalises that along the line in
 * *projected* (Mercator) space. Mercator stretches with latitude, so on a leg that changes latitude
 * a lot the two disagree: measured on Amsterdam to Sydney, the marker ran up to 1,681 km ahead of
 * the ink -- about a tenth of the route, zero at both ends and worst in the middle.
 *
 * Keeping the marker on geodesic distance is the right call: it should cross the map at a constant
 * real-world speed. So convert its position into the gradient's own units instead of the other way
 * round, and the ink follows it exactly.
 */
const mercatorY = (lat: number) => {
	const clamped = Math.max(-85, Math.min(85, lat));
	return Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360));
};

export const geodesicToProjectedFraction = (coords: number[][], geoFraction: number) => {
	if (!Array.isArray(coords) || coords.length < 2) return geoFraction;
	const geo = [0];
	const prj = [0];
	for (let i = 1; i < coords.length; i++) {
		const [lo1, la1] = coords[i - 1];
		const [lo2, la2] = coords[i];
		if (![lo1, la1, lo2, la2].every(isValidCoord)) return geoFraction;
		// Geodesic step, haversine, kilometres.
		const p1 = (la1 * Math.PI) / 180;
		const p2 = (la2 * Math.PI) / 180;
		const dP = p2 - p1;
		const dL = ((lo2 - lo1) * Math.PI) / 180;
		const h = Math.sin(dP / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dL / 2) ** 2;
		geo.push(geo[i - 1] + 2 * 6371 * Math.asin(Math.min(1, Math.sqrt(h))));
		// Projected step, in Mercator units, with the antimeridian handled.
		let dx = lo2 - lo1;
		if (dx > 180) dx -= 360;
		if (dx < -180) dx += 360;
		prj.push(prj[i - 1] + Math.hypot(dx, ((mercatorY(la2) - mercatorY(la1)) * 180) / Math.PI));
	}
	const totalGeo = geo[geo.length - 1];
	const totalPrj = prj[prj.length - 1];
	if (!(totalGeo > 0) || !(totalPrj > 0)) return geoFraction;

	const target = totalGeo * Math.max(0, Math.min(1, geoFraction));
	for (let i = 1; i < geo.length; i++) {
		if (geo[i] >= target) {
			const span = geo[i] - geo[i - 1];
			const r = span > 0 ? (target - geo[i - 1]) / span : 0;
			return (prj[i - 1] + (prj[i] - prj[i - 1]) * r) / totalPrj;
		}
	}
	return 1;
};

/**
 * Where a leg's ink ends and where its marker stands, at the eased progress `easedPath` along it.
 * `routeDistance` is `length(path)`, which the caller already has.
 */
export const legPhaseState = (path: any, routeDistance: number, easedPath: number) => ({
	// The marker keeps its geodesic timing; the ink is converted into line-progress units so the two
	// stay on the same point. See geodesicToProjectedFraction above.
	inkStop: geodesicToProjectedFraction(path?.geometry?.coordinates, easedPath),
	markerPosition: along(path, routeDistance * easedPath, { units: "kilometers" }).geometry.coordinates,
});

/** The gradient that inks a leg's line from its start up to `inkStop` and leaves the rest clear. */
export const legGradient = (color: string, inkStop: number) => ["step", ["line-progress"], color, inkStop, "rgba(0, 0, 0, 0)"];

/** Draw leg `index`'s line, inked up to `inkStop`, fully opaque. */
export const writeLegLine = (map: any, index: number, color: string, inkStop: number) => {
	map?.setPaintProperty(`line-${index}`, "line-gradient", legGradient(color, inkStop));
	map?.setPaintProperty(`line-${index}`, "line-opacity", 1);
};

/**
 * Put the transport marker (the dot, the vehicle on it, the avatar layer) where `position` is, in the
 * vehicle of the leg it travels.
 *
 * `opacity` is 1 while the marker is travelling, which is PathSequence. FinishedSequences passes 0: once a
 * leg is over, the stop that follows hides the marker again (MarkerSequence's first frame), so that is
 * what a render that ran the leg has on the map from then on. Leaving it at 1 there would show the
 * vehicle on the last stop under the outro's dissolve. `position` undefined leaves the source alone:
 * setData on a GeoJSON source is not a no-op for equal data, it sends the source back to a worker.
 */
export const writeTransportMarker = (map: any, { vehicle, scale, position, opacity }: { vehicle?: string; scale: number; position?: number[]; opacity: number }) => {
	map?.setPaintProperty("transportMarkerDot", "circle-opacity", opacity);
	map?.setPaintProperty("transportMarkerIcon", "icon-opacity", opacity);
	map?.setPaintProperty("transportProfilePicture", "icon-opacity", opacity);

	if (position) {
		map.getSource("transportMarkerSource").setData({
			type: "Feature",
			geometry: {
				type: "Point",
				coordinates: position,
			},
			properties: {},
		});
	}

	const transportIcon = `${vehicle}Icon`;

	if (transportIcon === "undefinedIcon") {
		// No transport icon - show small dot only
		map?.setLayoutProperty("transportMarkerIcon", "icon-image", "");
		map?.setPaintProperty("transportMarkerDot", "circle-opacity", opacity);
		map?.setPaintProperty("transportMarkerDot", "circle-radius", 12 * scale);
	} else {
		// Has transport icon - show icon with colored dot background
		map?.setLayoutProperty("transportMarkerIcon", "icon-image", transportIcon);
		map?.setLayoutProperty("transportMarkerIcon", "icon-size", 0.6 * scale);
		map?.setPaintProperty("transportMarkerDot", "circle-opacity", opacity);
		map?.setPaintProperty("transportMarkerDot", "circle-radius", 20 * scale);
	}
};
